import { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DollarSign, Droplets, Lightbulb, Package, Pencil, Receipt, TrendingDown, Trash2, Zap } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const CATEGORIES_BLACKFIT = [
  { key: 'salary', label: 'Salary', icon: Receipt },
  { key: 'alat', label: 'Alat', icon: Package },
  { key: 'pajak', label: 'Pajak', icon: DollarSign },
];

const CATEGORIES_BLACKFIT_PREMIERE = [
  ...CATEGORIES_BLACKFIT,
  { key: 'listrik', label: 'Listrik', icon: Zap },
  { key: 'air', label: 'Air', icon: Droplets },
  { key: 'abodemen_listrik', label: 'Abodemen Listrik', icon: Lightbulb },
];

const MERCHANT_OPTIONS = [
  { key: 'blackfit', label: 'Blackfit', categories: CATEGORIES_BLACKFIT },
  { key: 'blackfit_premiere', label: 'Blackfit Premiere', categories: CATEGORIES_BLACKFIT_PREMIERE },
];

const CARD_STYLES = {
  salary: {
    gradient: 'from-blue-500/[0.08] to-blue-600/[0.03]',
    border: 'border-blue-500/20',
    accent: 'text-blue-400',
    dot: 'bg-blue-400',
  },
  alat: {
    gradient: 'from-emerald-500/[0.08] to-emerald-600/[0.03]',
    border: 'border-emerald-500/20',
    accent: 'text-emerald-400',
    dot: 'bg-emerald-400',
  },
  pajak: {
    gradient: 'from-amber-500/[0.08] to-amber-600/[0.03]',
    border: 'border-amber-500/20',
    accent: 'text-amber-400',
    dot: 'bg-amber-400',
  },
  listrik: {
    gradient: 'from-yellow-500/[0.08] to-yellow-600/[0.03]',
    border: 'border-yellow-500/20',
    accent: 'text-yellow-400',
    dot: 'bg-yellow-400',
  },
  air: {
    gradient: 'from-cyan-500/[0.08] to-cyan-600/[0.03]',
    border: 'border-cyan-500/20',
    accent: 'text-cyan-400',
    dot: 'bg-cyan-400',
  },
  abodemen_listrik: {
    gradient: 'from-purple-500/[0.08] to-purple-600/[0.03]',
    border: 'border-purple-500/20',
    accent: 'text-purple-400',
    dot: 'bg-purple-400',
  },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MONTH_NAMES_ID = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

const formatMonthShort = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const m = parseInt(ym.slice(5, 7), 10);
  return `${MONTH_NAMES_ID[m - 1]} ${ym.slice(2, 4)}`;
};

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

const monthsBetweenInclusive = (startYm, endYm) => {
  if (!MONTH_PATTERN.test(startYm || '') || !MONTH_PATTERN.test(endYm || '')) return 0;
  const sy = parseInt(startYm.slice(0, 4), 10);
  const sm = parseInt(startYm.slice(5, 7), 10);
  const ey = parseInt(endYm.slice(0, 4), 10);
  const em = parseInt(endYm.slice(5, 7), 10);
  return Math.max(0, (ey - sy) * 12 + (em - sm) + 1);
};

const addMonths = (ym, n) => {
  if (!MONTH_PATTERN.test(ym || '')) return ym;
  let y = parseInt(ym.slice(0, 4), 10);
  let m = parseInt(ym.slice(5, 7), 10) + n;
  while (m > 12) { m -= 12; y += 1; }
  while (m < 1) { m += 12; y -= 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
};

const initialExpenseForm = {
  category: 'salary',
  amount: '',
  title: '',
  expense_date: new Date().toISOString().slice(0, 10),
  notes: '',
};

const initialDepreciationForm = {
  item_name: '',
  initial_balance: '',
  periods: '',
  start_month: '',
  end_month: '',
  monthly_amount: '',
  notes: '',
};

export default function Accounting() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';
  const [activeTab, setActiveTab] = useState('accrue');
  const [merchant, setMerchant] = useState(MERCHANT_OPTIONS[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialExpenseForm);
  const [submitting, setSubmitting] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const activeMerchant = MERCHANT_OPTIONS.find((m) => m.key === merchant) || MERCHANT_OPTIONS[0];
  const categories = activeMerchant.categories;

  const fetchAccrue = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/accounting/accrue?merchant=${activeMerchant.key}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Gagal mengambil data accrue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'accrue') fetchAccrue();
  }, [activeTab, activeMerchant.key]);

  const handleMerchantChange = (key) => {
    setMerchant(key);
    const next = MERCHANT_OPTIONS.find((m) => m.key === key);
    if (next && !next.categories.some((c) => c.key === formData.category)) {
      setFormData((prev) => ({ ...prev, category: next.categories[0].key }));
    }
  };

  const openAddModal = (category) => {
    const defaultCategory = categories.some((c) => c.key === category) ? category : categories[0].key;
    setEditingExpense(null);
    setFormData({ ...initialExpenseForm, category: defaultCategory });
    setIsModalOpen(true);
  };

  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category || 'salary',
      amount: expense.amount ?? '',
      title: expense.title || '',
      expense_date: (expense.expense_date || '').slice(0, 10),
      notes: expense.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.amount || Number(formData.amount) <= 0) {
      alert('Judul dan jumlah harus diisi.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        merchant: activeMerchant.key,
        category: formData.category,
        amount: Number(formData.amount),
        title: formData.title.trim(),
        expense_date: formData.expense_date,
        notes: formData.notes.trim(),
      };
      if (editingExpense) {
        await axios.put(`${API}/accounting/accrue/expenses/${editingExpense.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/accounting/accrue/expenses`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingExpense(null);
      setFormData(initialExpenseForm);
      fetchAccrue();
    } catch (err) {
      alert(err.response?.data?.detail || 'Gagal menyimpan expense.');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: 'accrue', label: 'Accrue' },
    { key: 'depreciation', label: 'Depreciation' },
  ];

  return (
    <DashboardLayout>
      <Header title="Accounting" />
      <div className="space-y-6 p-8" data-testid="accounting-page">
        <div className="flex gap-2 border-b border-white/10 pb-3">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-sm px-4 py-2 text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30'
                  : 'text-zinc-400 hover:text-zinc-200 border border-transparent hover:border-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          <div className="flex gap-2">
            {MERCHANT_OPTIONS.map((m) => (
              <button
                key={m.key}
                onClick={() => handleMerchantChange(m.key)}
                className={`rounded-sm px-4 py-2 text-sm font-medium transition-all ${
                  merchant === m.key
                    ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30'
                    : 'text-zinc-400 hover:text-zinc-200 border border-transparent hover:border-white/10'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {activeTab === 'accrue' && (
            <AccrueContent
              data={data}
              loading={loading}
              onAddExpense={openAddModal}
              onEditExpense={openEditModal}
              categories={categories}
              merchantLabel={activeMerchant.label}
            />
          )}
          {activeTab === 'depreciation' && <DepreciationContent merchant={activeMerchant.key} merchantLabel={activeMerchant.label} />}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md border-white/10 bg-zinc-950 text-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {editingExpense ? 'Edit Expense' : 'Tambah Expense'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Category</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="mt-1 w-full rounded-sm border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white focus:border-[#D4AF37]/50 focus:outline-none"
              >
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Judul</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Judul expense"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Jumlah (Rp)</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Tanggal (Backdate)</Label>
              <Input
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                className="mt-1 border-white/10 bg-zinc-900 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Catatan</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Opsional"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="border-white/10 bg-black/30 text-white hover:bg-zinc-800"
              >
                Batal
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-[#D4AF37] text-black hover:bg-[#C59A27]">
                {submitting ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function AccrueContent({ data, loading, onAddExpense, onEditExpense, categories, merchantLabel }) {
  const sortedExpenses = useMemo(() => {
    return [...(data?.expenses || [])].sort(
      (a, b) => String(b.expense_date || '').localeCompare(String(a.expense_date || ''))
    );
  }, [data?.expenses]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
      </div>
    );
  }

  if (!data) return null;

  const { balances, months, category_monthly_accrual, category_monthly_expenses } = data;

  const buildTableData = () => {
    return categories.map((cat) => {
      const bal = balances?.[cat.key] || {};
      const initial = bal.initial_balance || 0;
      const monthlyAccrual = category_monthly_accrual?.[cat.key] || {};
      const monthlyExpenses = category_monthly_expenses?.[cat.key] || {};

      let runningBalance = initial;
      const monthData = months.map((m) => {
        const acc = monthlyAccrual[m] || 0;
        const used = monthlyExpenses[m] || 0;
        runningBalance = runningBalance + acc - used;
        return { month: m, acc, used, cutOff: runningBalance };
      });

      return { category: cat, initial, months: monthData };
    });
  };

  const tableData = buildTableData();

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {categories.map((cat) => {
          const bal = balances?.[cat.key] || {};
          const s = CARD_STYLES[cat.key] || CARD_STYLES.salary;
          return (
            <div
              key={cat.key}
              className={`relative overflow-hidden rounded-lg border ${s.border} bg-gradient-to-br ${s.gradient} p-5`}
            >
              <div className="absolute top-0 right-0 h-20 w-20 translate-x-6 -translate-y-6 rounded-full bg-white/[0.03]" />
              <div className="flex items-center gap-2 mb-4">
                <div className={`h-2 w-2 rounded-full ${s.dot}`} />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
                  {cat.label}
                </span>
              </div>
              <p className={`text-2xl font-bold tracking-tight ${s.accent}`}>
                {formatCurrency(bal.current_balance)}
              </p>
              <div className="mt-2 flex items-center gap-3 text-[11px] text-zinc-500">
                <span>Saldo Awal {formatCurrency(bal.initial_balance)}</span>
              </div>
              <div className="mt-1 flex items-center gap-4 text-xs">
                <span className="text-emerald-400/80">+{formatCurrency(bal.monthly_accrual)}/bln</span>
                {bal.total_expenses > 0 && (
                  <span className="text-red-400/80">-{formatCurrency(bal.total_expenses)}</span>
                )}
              </div>
              <button
                onClick={() => onAddExpense(cat.key)}
                className="mt-3 inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-zinc-400 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
              >
                + Tambah Pengeluaran
              </button>
            </div>
          );
        })}
      </div>

      {/* Monthly Table */}
      <div className="rounded-lg border border-white/[0.06] bg-gradient-to-b from-white/[0.02] to-transparent">
        <div className="border-b border-white/[0.06] px-5 py-3.5">
          <h3 className="text-sm font-semibold text-white/90">
            Saldo Accrue Bulanan{merchantLabel ? ` — ${merchantLabel}` : ''}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th rowSpan={2} className="sticky left-0 z-20 bg-zinc-950 px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                  Item
                </th>
                <th rowSpan={2} className="sticky left-[88px] z-20 bg-zinc-950 px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                  Saldo Awal
                </th>
                {months.map((m) => (
                  <th
                    key={m + '-grp'}
                    colSpan={3}
                    className="border-l border-white/[0.08] px-2 py-2 text-center text-[10px] font-bold uppercase tracking-[0.1em] text-[#D4AF37]"
                  >
                    {formatMonthShort(m)}
                  </th>
                ))}
              </tr>
              <tr className="border-b border-white/[0.06]">
                {months.map((m) => (
                  <Fragment key={m + '-sub'}>
                    <th className="border-l border-white/[0.04] px-2 py-2 text-right text-[9px] font-semibold uppercase tracking-[0.06em] text-zinc-500">
                      Tambahan
                    </th>
                    <th className="px-2 py-2 text-right text-[9px] font-semibold uppercase tracking-[0.06em] text-zinc-500">
                      Penggunaan
                    </th>
                    <th className="bg-emerald-500/[0.08] px-2 py-2 text-right text-[9px] font-semibold uppercase tracking-[0.06em] text-emerald-400">
                      Saldo
                    </th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, idx) => (
                <tr
                  key={row.category.key}
                  className={`border-b border-white/[0.04] transition-colors hover:bg-white/[0.02] ${idx % 2 === 0 ? 'bg-white/[0.01]' : ''}`}
                >
                  <td className="sticky left-0 z-10 bg-zinc-950 px-4 py-3 font-medium text-zinc-300">
                    {row.category.label}
                  </td>
                  <td className="sticky left-[88px] z-10 bg-zinc-950 px-4 py-3 text-right font-semibold text-white">
                    {formatCurrency(row.initial)}
                  </td>
                  {row.months.map((md) => (
                    <Fragment key={md.month + '-cells'}>
                      <td className="border-l border-white/[0.04] px-2 py-3 text-right text-emerald-400/90">
                        {formatCurrency(md.acc)}
                      </td>
                      <td className={`px-2 py-3 text-right ${md.used > 0 ? 'text-red-400/90' : 'text-zinc-600'}`}>
                        {formatCurrency(md.used)}
                      </td>
                      <td className="bg-emerald-500/[0.06] px-2 py-3 text-right font-semibold text-emerald-300">
                        {formatCurrency(md.cutOff)}
                      </td>
                    </Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expense List */}
      <div className="rounded-lg border border-white/[0.06] bg-gradient-to-b from-white/[0.02] to-transparent">
        <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-3.5">
          <h3 className="text-sm font-semibold text-white/90">Pengeluaran Accrue</h3>
          <Button onClick={() => onAddExpense('salary')} size="sm" className="bg-[#D4AF37] text-black hover:bg-[#C59A27] rounded-md h-8 text-xs font-medium">
            + Tambah
          </Button>
        </div>
        {sortedExpenses.length === 0 ? (
          <div className="py-14 text-center">
            <Receipt className="mx-auto mb-2 h-8 w-8 text-zinc-700" />
            <p className="text-sm text-zinc-500">Belum ada pengeluaran</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Tanggal</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Judul</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Category</th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Jumlah</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Catatan</th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {sortedExpenses.map((exp, idx) => (
                  <tr
                    key={exp.id}
                    className={`border-b border-white/[0.04] transition-colors hover:bg-white/[0.02] ${idx % 2 === 0 ? 'bg-white/[0.01]' : ''}`}
                  >
                    <td className="px-5 py-3 text-zinc-400">{formatDate(exp.expense_date)}</td>
                    <td className="px-5 py-3 font-medium text-white">{exp.title}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center rounded-md bg-white/5 px-2 py-0.5 text-xs font-medium text-zinc-300 capitalize">
                        {exp.category}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-red-400">{formatCurrency(exp.amount)}</td>
                    <td className="px-5 py-3 text-zinc-500 max-w-[200px] truncate">{exp.notes || '-'}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => onEditExpense(exp)}
                        className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-medium text-zinc-400 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DepreciationContent({ merchant, merchantLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialDepreciationForm);
  const [submitting, setSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchDepreciation = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/accounting/depreciation?merchant=${merchant}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Gagal mengambil data depresiasi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepreciation();
  }, [merchant]);

  const openAdd = () => {
    setEditingItem(null);
    setFormData(initialDepreciationForm);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      item_name: item.item_name || '',
      initial_balance: item.initial_balance ?? '',
      periods: item.periods ?? '',
      start_month: item.start_month || '',
      end_month: item.end_month || '',
      monthly_amount: item.monthly_amount ?? '',
      notes: item.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Hapus depresiasi "${item.item_name}"?`)) return;
    try {
      await axios.delete(`${API}/accounting/depreciation/${item.id}`, { withCredentials: true });
      fetchDepreciation();
    } catch (err) {
      alert(err.response?.data?.detail || 'Gagal menghapus depresiasi.');
    }
  };

  const handleStartMonthChange = (value) => {
    const start = value;
    const end = formData.end_month;
    const periods = formData.periods;
    if (MONTH_PATTERN.test(end || '')) {
      setFormData({ ...formData, start_month: start, periods: String(monthsBetweenInclusive(start, end) || '') });
    } else if (periods && Number(periods) >= 1 && MONTH_PATTERN.test(start || '')) {
      setFormData({ ...formData, start_month: start, end_month: addMonths(start, Number(periods) - 1) });
    } else {
      setFormData({ ...formData, start_month: start });
    }
  };

  const handleEndMonthChange = (value) => {
    const end = value;
    const start = formData.start_month;
    if (MONTH_PATTERN.test(start || '')) {
      setFormData({ ...formData, end_month: end, periods: String(monthsBetweenInclusive(start, end) || '') });
    } else {
      setFormData({ ...formData, end_month: end });
    }
  };

  const handlePeriodsChange = (value) => {
    const periods = value;
    const start = formData.start_month;
    const end = formData.end_month;
    if (MONTH_PATTERN.test(start || '') && Number(periods) >= 1) {
      setFormData({ ...formData, periods, end_month: addMonths(start, Number(periods) - 1) });
    } else if (MONTH_PATTERN.test(end || '') && Number(periods) >= 1) {
      setFormData({ ...formData, periods, start_month: addMonths(end, -(Number(periods) - 1)) });
    } else {
      setFormData({ ...formData, periods });
    }
  };

  const handleSubmit = async () => {
    const initialBalance = Number(formData.initial_balance);
    const monthlyAmount = Number(formData.monthly_amount);
    const periods = Number(formData.periods);
    if (!formData.item_name.trim()) {
      alert('Nama item harus diisi.');
      return;
    }
    if (!initialBalance || initialBalance <= 0) {
      alert('Saldo awal harus diisi dengan angka lebih dari 0.');
      return;
    }
    if (!monthlyAmount || monthlyAmount <= 0) {
      alert('Nominal per bulan harus diisi dengan angka lebih dari 0.');
      return;
    }
    if (!periods || periods < 1) {
      alert('Jumlah periode minimal 1.');
      return;
    }
    if (!MONTH_PATTERN.test(formData.start_month || '') || !MONTH_PATTERN.test(formData.end_month || '')) {
      alert('Format bulan harus YYYY-MM.');
      return;
    }
    if (formData.end_month < formData.start_month) {
      alert('Bulan berakhir harus setelah bulan mulai.');
      return;
    }
    if (monthsBetweenInclusive(formData.start_month, formData.end_month) !== periods) {
      alert('Jumlah periode harus sama dengan rentang bulan mulai sampai berakhir.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        item_name: formData.item_name.trim(),
        initial_balance: initialBalance,
        periods,
        start_month: formData.start_month,
        end_month: formData.end_month,
        monthly_amount: monthlyAmount,
        notes: formData.notes.trim(),
      };
      if (editingItem) {
        await axios.put(`${API}/accounting/depreciation/${editingItem.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/accounting/depreciation`, { ...payload, merchant }, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingItem(null);
      setFormData(initialDepreciationForm);
      fetchDepreciation();
    } catch (err) {
      alert(err.response?.data?.detail || 'Gagal menyimpan depresiasi.');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    if (status === 'berjalan') return <span className="inline-flex items-center rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400">Berjalan</span>;
    if (status === 'selesai') return <span className="inline-flex items-center rounded-md bg-zinc-500/15 px-2 py-0.5 text-xs font-medium text-zinc-400">Selesai</span>;
    return <span className="inline-flex items-center rounded-md bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-400">Belum Mulai</span>;
  };

  const items = data?.items || [];
  const monthlyHintTotal = Number(formData.periods) >= 1 && Number(formData.monthly_amount) > 0
    ? Number(formData.periods) * Number(formData.monthly_amount)
    : null;

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-white/90">Depresiasi{merchantLabel ? ` — ${merchantLabel}` : ''}</h3>
          <p className="mt-1 text-xs text-zinc-500">Saldo berkurang otomatis setiap bulan sampai periode berakhir. Item aktif masuk ke Laporan Bulanan.</p>
        </div>
        <Button onClick={openAdd} size="sm" className="bg-[#D4AF37] text-black hover:bg-[#C59A27] rounded-md h-8 text-xs font-medium">
          + Tambah Depresiasi
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-white/[0.06] bg-gradient-to-b from-white/[0.02] to-transparent py-14 text-center">
          <TrendingDown className="mx-auto mb-2 h-8 w-8 text-zinc-700" />
          <p className="text-sm text-zinc-500">Belum ada depresiasi</p>
        </div>
      ) : (
        <div className="rounded-lg border border-white/[0.06] bg-gradient-to-b from-white/[0.02] to-transparent">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Item</th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Saldo Awal</th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">per Bulan</th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Periode</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Mulai</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Berakhir</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Sisa Saldo</th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr
                    key={item.id}
                    className={`border-b border-white/[0.04] transition-colors hover:bg-white/[0.02] ${idx % 2 === 0 ? 'bg-white/[0.01]' : ''}`}
                  >
                    <td className="px-5 py-3 font-medium text-white">{item.item_name}</td>
                    <td className="px-5 py-3 text-right text-zinc-300">{formatCurrency(item.initial_balance)}</td>
                    <td className="px-5 py-3 text-right text-emerald-400/90">{formatCurrency(item.monthly_amount)}</td>
                    <td className="px-5 py-3 text-right text-zinc-400">{item.periods} bln</td>
                    <td className="px-5 py-3 text-zinc-400">{formatMonthShort(item.start_month)}</td>
                    <td className="px-5 py-3 text-zinc-400">{formatMonthShort(item.end_month)}</td>
                    <td className="px-5 py-3">{statusBadge(item.status)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-white">{formatCurrency(item.remaining_balance)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEdit(item)}
                          className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-medium text-zinc-400 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-medium text-zinc-400 transition hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400"
                        >
                          <Trash2 className="h-3 w-3" />
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md border-white/10 bg-zinc-950 text-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {editingItem ? 'Edit Depresiasi' : 'Tambah Depresiasi'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Item</Label>
              <Input
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                placeholder="Nama item"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Saldo Awal (Rp)</Label>
              <Input
                type="number"
                value={formData.initial_balance}
                onChange={(e) => setFormData({ ...formData, initial_balance: e.target.value })}
                placeholder="0"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Nominal per Bulan (Rp)</Label>
              <Input
                type="number"
                value={formData.monthly_amount}
                onChange={(e) => setFormData({ ...formData, monthly_amount: e.target.value })}
                placeholder="0"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Periode (bln)</Label>
                <Input
                  type="number"
                  value={formData.periods}
                  onChange={(e) => handlePeriodsChange(e.target.value)}
                  placeholder="0"
                  className="mt-1 border-white/10 bg-zinc-900 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Mulai</Label>
                <Input
                  type="month"
                  value={formData.start_month}
                  onChange={(e) => handleStartMonthChange(e.target.value)}
                  className="mt-1 border-white/10 bg-zinc-900 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Berakhir</Label>
                <Input
                  type="month"
                  value={formData.end_month}
                  onChange={(e) => handleEndMonthChange(e.target.value)}
                  className="mt-1 border-white/10 bg-zinc-900 text-white"
                />
              </div>
            </div>
            {monthlyHintTotal !== null && (
              <p className="text-[11px] text-zinc-500">
                Total penyusutan: {Number(formData.periods)} × {formatCurrency(formData.monthly_amount)} = {formatCurrency(monthlyHintTotal)}
                {Number(formData.initial_balance) > 0 && monthlyHintTotal !== Number(formData.initial_balance) && (
                  <span className="text-amber-400/80"> (berbeda dari saldo awal {formatCurrency(formData.initial_balance)})</span>
                )}
              </p>
            )}
            <div>
              <Label className="text-zinc-400 text-xs">Catatan</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Opsional"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="border-white/10 bg-black/30 text-white hover:bg-zinc-800"
              >
                Batal
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-[#D4AF37] text-black hover:bg-[#C59A27]">
                {submitting ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
